import { ChakraProvider } from '@chakra-ui/react'
import { SessionProvider as AuthProvider } from 'next-auth/react'
import type { AppProps } from 'next/app'
import getConfig from 'next/config'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import NextNProgress from 'nextjs-progressbar'
import { Analytics } from '@vercel/analytics/next'
import { SpeedInsights } from '@vercel/speed-insights/next'
import { GlobalTwoFactorVerificationModal } from 'blocks/two-factor-verification/TwoFactorVerificationModal'
import PostHogProvider from 'pages/providers/posthog'
import { routes } from 'routes'
import { trpc } from 'lib/trpc'
import { KikinHead } from 'components/KikinHead'
import { PageTransitionOverlay } from 'components/PageTransitionOverlay'
import { SentryUserManager } from 'components/sentry/SentryUserManager'
import { customTheme } from 'theme/theme'

const { publicRuntimeConfig } = getConfig()

const LazyConfigureGocardlessEventRedirectionBanner = dynamic(
    () => import('components/gocardless/ConfigureGocardlessEventRedirectionBanner'),
    { loading: () => null, ssr: false },
)

function MyApp({ Component, pageProps: { session, ...pageProps } }: AppProps) {
    const router = useRouter()

    const isProdEnvironment = process.env.NODE_ENV === 'production'

    if (router.pathname === routes.calculator()) {
        return <Component {...pageProps} />
    }

    if (router.pathname === routes.impact()) {
        return (
            <ChakraProvider theme={customTheme}>
                <Component {...pageProps} />
            </ChakraProvider>
        )
    }

    return (
        <>
            {publicRuntimeConfig.showGocardlessConfigRedirectBanner && (
                <LazyConfigureGocardlessEventRedirectionBanner />
            )}
            <PageTransitionOverlay />
            <NextNProgress color={customTheme.colors.neon[100]} showOnShallow={false} />
            <KikinHead />
            <Analytics />
            <AuthProvider session={session}>
                <PostHogProvider>
                    <>
                        <SentryUserManager />
                        <ChakraProvider theme={customTheme}>
                            <Component {...pageProps} />
                            <GlobalTwoFactorVerificationModal />
                            <SpeedInsights />
                        </ChakraProvider>
                    </>
                </PostHogProvider>
            </AuthProvider>
        </>
    )
}

export default trpc.withTRPC(MyApp)
